// Today's Print Queue — T1.11 skeleton + T3.5 real data wire + T3.6 Karma sort.
//
// Phase 3 T3.5: replaces T1.11 empty-state placeholder with TanStack Query
// fetch of pending shipments + table render + per-row Print A4 placeholder.
//
// Phase 3 T3.6 (Karma queue pattern — Volkan brainstorming Soru 8 ratify):
//   sistem (PEXIFY engine) önerir + Emin override pins.
//   - Pin/Unpin button per row (Zustand session-local pins).
//   - Karma sort: pinned first (in pin order), unpinned via sistem priority.
//   - Visual indicator (Pin icon + pinnedBadge) on pinned rows.
//
// Data source: GET /api/v1/shipments?state=pending (MSW handler extended in
// T3.5 with ?state= filter; existing ?order_id= preserved). Envelope:
// { data: Shipment[], meta: { total } }. Pending = awaiting label print +
// packaging dispatch; in_transit/delivered/voided excluded from queue.
//
// Print A4 button (health-tour Wave 3.6, Volkan "bağla" 2026-08-09): resolves
// the shipment's order → work order, generates the dual packing slips via the
// warehouse endpoint, and downloads the Emin (FULL) a4_slip PDF. Mock mode
// (no real BE) falls through to an informational toast.
//
// Surface: mounted under Supply Shell (per T1.10 adaptation — dedicated
// /warehouse shell deferred to Wave-16 Madde 15). Sidebar entry conditional
// on ship.shipment.scan capability so Mehmet/Hakan SO2 + Emin/Aybars SO3
// don't see the link; Emin warehouse_operator does.
//
// Capability gate: ship.shipment.scan (A-32 §A7 — V2 amendment). Resolution
// uses the SupplyPersona store since this route lives at /supply/print-queue.
// Volkan A1 (admin) inherits all capabilities via Ring 3 bypass.
//
// Anchors: feedback_a32_capability_resolution_v1
//        + feedback_shipping_v1_mini_batch_2_guard_closure_2026-06-05
//        + reference_persona_rename_aybars_to_emin_2026_06_04

import { useMemo, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Printer, Calendar, PackageOpen, Pin, PinOff, Plus } from 'lucide-react'
import { apiUrl } from '../../lib/api'
import { toast } from '../../lib/toast'
import { useT } from '../../i18n'
import { todaysPrintQueueI18n } from '../../i18n/supply/todays-print-queue'
import { useSupplyPersonaStore } from '../../store/role'
import { usePrintQueuePinsStore } from '../../store/print-queue-pins'
import { applyKarmaSort } from '../../lib/shipping/print-queue-priority'
import {
  hasCapability,
  resolveCurrentUser,
} from '../../lib/user-permissions'
import type { Shipment } from '../../mocks/data/shipments'
import { DataTableShell } from '../../components/ui/data-table-shell'

type ShipmentsListResponse = {
  data: Shipment[]
  meta: { total: number }
}

export function TodaysPrintQueue() {
  const t = useT(todaysPrintQueueI18n)
  const supplyPersona = useSupplyPersonaStore((s) => s.supplyPersona)
  const me = resolveCurrentUser({ role: 'supply', supplyPersona })
  const canScan = hasCapability(me.user_id, 'ship.shipment.scan')

  const queryClient = useQueryClient()
  const pinnedIdsList = usePrintQueuePinsStore((s) => s.pinnedIds)
  const pin = usePrintQueuePinsStore((s) => s.pin)
  const unpin = usePrintQueuePinsStore((s) => s.unpin)

  const queue = useQuery({
    queryKey: ['warehouse', 'todays-print-queue', 'pending'],
    queryFn: async (): Promise<ShipmentsListResponse> => {
      const res = await fetch(apiUrl('/admin/shipments?state=pending'))
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      // BE returns the C2-canonical { items, total } envelope; adapt to the
      // local { data, meta: { total } } shape the rest of the component reads.
      const body = (await res.json()) as { items: Shipment[]; total: number }
      return { data: body.items, meta: { total: body.total } }
    },
    enabled: canScan,
  })

  // T3.9 multi-piece "+" mutation.  Posts to /orders/:id/shipments/add and
  // invalidates the queue query on success so the new pending row appears.
  const addPieceMutation = useMutation({
    mutationFn: async (orderId: string) => {
      const res = await fetch(apiUrl(`/orders/${orderId}/shipments/add`), {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: '{}',
      })
      if (!res.ok) throw new Error(`HTTP ${res.status}`)
      return res.json()
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({
        queryKey: ['warehouse', 'todays-print-queue', 'pending'],
      })
    },
  })

  // Print A4 (Wave 3.6): shipment → order's work order → generate dual slips →
  // download the Emin FULL variant. Any failure (incl. maket mode, where the
  // warehouse endpoints don't exist) surfaces the informational toast instead
  // of a silent no-op.
  const [printingId, setPrintingId] = useState<string | null>(null)

  async function handlePrintA4(shipment: Shipment): Promise<void> {
    if (printingId) return
    setPrintingId(shipment.id)
    try {
      const woRes = await fetch(apiUrl(`/admin/work-orders?order_id=${shipment.order_id}`))
      if (!woRes.ok) throw new Error(`HTTP ${woRes.status}`)
      const woBody = (await woRes.json()) as { items?: Array<{ id: string }> }
      const wo = woBody.items?.[0]
      if (!wo) {
        toast.info(t.printNoWorkOrderTitle, {
          description: t.printNoWorkOrderDesc,
          audience: 'supply',
        })
        return
      }
      const genRes = await fetch(
        apiUrl(`/warehouse/work-orders/${wo.id}/packing-slips?format=a4_slip`),
        { method: 'POST' },
      )
      if (!genRes.ok) throw new Error(`HTTP ${genRes.status}`)
      const gen = (await genRes.json()) as {
        slips: Array<{ variant: string; download_url: string }>
      }
      const slip = gen.slips.find((x) => x.variant === 'emin') ?? gen.slips[0]
      if (!slip) throw new Error('no-slip')
      const pdfPath = slip.download_url.startsWith('/api/v1')
        ? apiUrl(slip.download_url.slice('/api/v1'.length))
        : slip.download_url
      const pdfRes = await fetch(pdfPath)
      if (!pdfRes.ok) throw new Error(`HTTP ${pdfRes.status}`)
      const blob = await pdfRes.blob()
      if (!blob.type.includes('pdf')) throw new Error('not-pdf')
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `packing-slip-a4-${shipment.id}.pdf`
      document.body.appendChild(a)
      a.click()
      a.remove()
      URL.revokeObjectURL(url)
    } catch {
      toast.info(t.printMockTitle, {
        description: t.printMockDesc,
        audience: 'supply',
      })
    } finally {
      setPrintingId(null)
    }
  }

  const items = queue.data?.data ?? []
  const pinnedIds = useMemo(() => new Set(pinnedIdsList), [pinnedIdsList])
  // Reorder input so pinned shipments precede non-pinned within applyKarmaSort
  // — preserves the user's pin sequence (movePinUp/Down) at render time.
  const inputOrdered = useMemo(() => {
    const byId = new Map(items.map((s) => [s.id, s]))
    const pinnedOrdered = pinnedIdsList
      .map((id) => byId.get(id))
      .filter((s): s is Shipment => s !== undefined)
    const rest = items.filter((s) => !pinnedIds.has(s.id))
    return [...pinnedOrdered, ...rest]
  }, [items, pinnedIdsList, pinnedIds])
  const sortedItems = useMemo(
    () => applyKarmaSort(inputOrdered, pinnedIds),
    [inputOrdered, pinnedIds],
  )

  if (!canScan) {
    return (
      <div className="mx-auto max-w-2xl">
        <div className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] p-6 text-sm text-[var(--color-text-muted)]">
          {t.forbidden}
        </div>
      </div>
    )
  }

  return (
    <div className="mx-auto max-w-6xl space-y-6">
      <header className="space-y-2">
        <h1 className="flex items-center gap-2 text-xl font-semibold text-[var(--color-text)]">
          <Printer size={20} className="text-indigo-600" aria-hidden="true" />
          {t.title}
        </h1>
        <p className="text-sm text-[var(--color-text-muted)]">{t.subtitle}</p>
        <div
          data-testid="date-filter-placeholder"
          className="pointer-events-none mt-2 inline-flex select-none items-center gap-2 rounded-md border border-dashed border-[var(--color-border-strong)] bg-[var(--color-surface-muted)] px-3 py-1.5 text-xs text-[var(--color-text-muted)] opacity-60"
        >
          <Calendar size={14} aria-hidden="true" />
          {t.dateFilterPlaceholder}
        </div>
      </header>

      <section className="rounded-lg border border-[var(--color-border)] bg-[var(--color-surface)] shadow-sm">
        {queue.isLoading ? (
          <div className="p-6 text-sm text-[var(--color-text-muted)]">{t.loading}</div>
        ) : queue.isError ? (
          <div className="p-6 text-sm text-[var(--color-danger)]">{t.errorState}</div>
        ) : items.length === 0 ? (
          <div className="flex flex-col items-center gap-3 p-10 text-center text-sm text-[var(--color-text-muted)]">
            <PackageOpen size={28} className="text-[var(--color-text-muted)]" aria-hidden="true" />
            <p>{t.emptyState}</p>
          </div>
        ) : (
          <DataTableShell variant="embedded">
          <table>
            <thead>
              <tr>
                <th>{t.columnShipment}</th>
                <th>{t.columnOrder}</th>
                <th>{t.columnPackages}</th>
                <th>{t.columnWeight}</th>
                <th>{t.columnCreated}</th>
                <th>{t.columnActions}</th>
              </tr>
            </thead>
            <tbody>
              {sortedItems.map((s) => {
                const pinned = pinnedIds.has(s.id)
                return (
                  <tr
                    key={s.id}
                    data-pinned={pinned ? 'true' : 'false'}
                    className={
                      pinned
                        ? '!bg-[var(--color-surface-muted)] hover:!bg-[var(--color-surface-raised)]'
                        : undefined
                    }
                  >
                    <td className="py-3 font-mono text-xs text-[var(--color-text)]">
                      <span className="inline-flex items-center gap-2">
                        {pinned && (
                          <span
                            aria-label={t.pinnedBadge}
                            title={t.pinnedBadge}
                            className="inline-flex items-center gap-1 rounded bg-[var(--color-surface)] px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-[var(--color-warning-foreground)]"
                          >
                            <Pin size={10} aria-hidden="true" />
                            {t.pinnedBadge}
                          </span>
                        )}
                        {s.id}
                      </span>
                    </td>
                    <td className="py-3 font-mono text-xs text-[var(--color-text)]">
                      {s.order_id}
                    </td>
                    <td className="py-3 tabular-nums text-[var(--color-text)]">
                      {s.package_count}
                    </td>
                    <td className="py-3 tabular-nums text-[var(--color-text)]">
                      {s.total_weight_oz !== null
                        ? `${s.total_weight_oz} ${t.weightUnit}`
                        : '—'}
                    </td>
                    <td className="py-3 text-[var(--color-text-muted)]">
                      {new Date(s.created_at).toLocaleDateString()}
                    </td>
                    <td className="py-3">
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() =>
                            pinned ? unpin(s.id) : pin(s.id)
                          }
                          aria-label={pinned ? t.actionUnpin : t.actionPin}
                          className={
                            pinned
                              ? 'inline-flex items-center gap-1.5 rounded border border-[var(--color-warning)] bg-[var(--color-surface)] px-2 py-1 text-xs font-medium text-[var(--color-warning-foreground)] hover:bg-[var(--color-surface-muted)]'
                              : 'inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs font-medium text-[var(--color-text)] hover:bg-[var(--color-surface-muted)]'
                          }
                        >
                          {pinned ? (
                            <PinOff size={12} aria-hidden="true" />
                          ) : (
                            <Pin size={12} aria-hidden="true" />
                          )}
                          {pinned ? t.actionUnpin : t.actionPin}
                        </button>
                        <button
                          type="button"
                          onClick={() => void handlePrintA4(s)}
                          disabled={printingId !== null}
                          className="inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs font-medium text-[var(--color-info)] hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
                        >
                          <Printer size={12} aria-hidden="true" />
                          {t.actionPrintA4}
                        </button>
                        <button
                          type="button"
                          onClick={() => addPieceMutation.mutate(s.order_id)}
                          disabled={addPieceMutation.isPending}
                          aria-label={t.actionAddPiece}
                          title={t.multiPieceTooltip}
                          className="inline-flex items-center gap-1.5 rounded border border-[var(--color-border)] bg-[var(--color-surface)] px-2 py-1 text-xs font-medium text-[var(--color-success-foreground)] hover:bg-[var(--color-surface-muted)] disabled:opacity-50"
                        >
                          <Plus size={12} aria-hidden="true" />
                          {t.actionAddPiece}
                        </button>
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
          </DataTableShell>
        )}
      </section>
    </div>
  )
}
